/**
 * Void Tree Screen — ÁRVORE de verdade (10.5L). Uma branch por vez (abas POWER /
 * SURVIVAL / AGILITY), nós dispostos por TIER (linhas) e fios ligando PAI → FILHOS
 * (SVG medido após o layout). Estados: LOCKED / AVAILABLE / UNLOCKED / MAX, detalhe por
 * toque (bottom sheet) e RESET com confirmação. Mobile: scroll vertical, alvos grandes.
 */
import { i18n } from "../i18n/i18n";

export type NodeState = "locked" | "available" | "unlocked" | "max";
export type TreeBranch = "power" | "survival" | "agility" | "class";

export interface TreeNodeView {
  id: string;
  branch: TreeBranch;
  tier: number;
  parent: string | null;
  name: string;
  rank: number;
  maxRank: number;
  cost: number;
  state: NodeState;
  desc: string;
  nextDesc: string;
  maxDesc: string; // 10.9EH — total acumulado no rank MAXIMO (nos de rank 50 nao diziam onde chegam)
  requires: string[]; // nomes dos pré-requisitos não atendidos
  icon: string;
}
export interface TreeViewData {
  available: number;
  nodes: TreeNodeView[];
}
export interface TreeHandlers {
  onUnlock: (id: string, times: number) => void; // 10.9EG — compra em lote
  onReset: () => void;
  onClose: () => void;
  getResetCost?: () => number; // 10.9Y — custo em 💎 do próximo reset (0 = grátis)
}

const BRANCHES: TreeBranch[] = ["power", "survival", "agility", "class"]; // 10.9CL — aba da CLASSE
const BRANCH_ICON: Record<TreeBranch, string> = { power: "⚔️", survival: "🛡️", agility: "👟", class: "✨" };

export class VoidTreeScreen {
  private readonly root: HTMLDivElement;
  private readonly tabsEl: HTMLElement;
  private readonly tree: HTMLElement;
  private readonly sheet: HTMLElement;
  private readonly confirm: HTMLElement;
  private provider: (() => TreeViewData) | null = null;
  private selected: string | null = null;
  private branch: TreeBranch = "power";

  constructor(uiRoot: HTMLElement, private readonly h: TreeHandlers) {
    this.root = document.createElement("div");
    this.root.className = "overlay tree-overlay";
    this.root.hidden = true;
    this.root.innerHTML = `
      <div class="panel tree-panel void-panel">
        <div class="tr-head">
          <h2 class="tr-title"></h2>
          <div class="tr-points"><span class="tr-pts-lbl"></span> <b class="tr-pts"></b></div>
          <button class="btn tr-x" data-a="close">✕</button>
        </div>
        <div class="tr-tabs"></div>
        <div class="tr-tree"><svg class="tr-links" aria-hidden="true"></svg></div>
        <div class="tr-hint" hidden></div>
        <div class="tr-foot"><button class="btn tr-reset" data-a="reset"></button></div>
      </div>
      <div class="tr-sheet" hidden></div>
      <div class="tr-confirm-wrap" hidden><div class="tr-confirm void-panel">
        <p class="trc-text"></p>
        <div class="trc-btns"><button class="btn primary" data-a="yes"></button><button class="btn" data-a="no"></button></div>
      </div></div>`;
    uiRoot.appendChild(this.root);
    this.tabsEl = this.root.querySelector(".tr-tabs")!;
    this.tree = this.root.querySelector(".tr-tree")!;
    this.sheet = this.root.querySelector(".tr-sheet")!;
    this.confirm = this.root.querySelector(".tr-confirm-wrap")!;
    this.root.querySelector('[data-a="close"]')!.addEventListener("click", () => this.h.onClose());
    this.root.querySelector('[data-a="reset"]')!.addEventListener("click", () => this.showConfirm());
    this.confirm.querySelector('[data-a="yes"]')!.addEventListener("click", () => {
      this.confirm.hidden = true;
      this.h.onReset();
    });
    this.confirm.querySelector('[data-a="no"]')!.addEventListener("click", () => (this.confirm.hidden = true));
    i18n.onChange(() => { if (!this.root.hidden) this.render(); });
  }

  open(provider: () => TreeViewData): void {
    this.provider = provider;
    this.selected = null;
    this.sheet.hidden = true;
    this.confirm.hidden = true;
    this.root.hidden = false;
    this.render();
  }
  hide(): void { this.root.hidden = true; }
  refresh(): void { if (!this.root.hidden) this.render(); }

  private render(): void {
    if (!this.provider) return;
    const data = this.provider();
    this.root.querySelector(".tr-title")!.textContent = i18n.t("voidTree.title");
    this.root.querySelector(".tr-pts-lbl")!.textContent = i18n.t("voidTree.points");
    this.root.querySelector(".tr-pts")!.textContent = String(data.available);
    this.root.querySelector(".tr-reset")!.textContent = "↺ " + i18n.t("voidTree.reset");
    // 10.9FE — tem ponto pra gastar e NADA aberto ainda: diz por onde começar
    const hint = this.root.querySelector<HTMLElement>(".tr-hint")!;
    const nothing = data.nodes.every((n) => n.rank === 0);
    hint.hidden = !(data.available > 0 && nothing);
    hint.textContent = "👆 " + i18n.t("voidTree.startHint");

    // abas por branch (com quantos nós já abertos em cada uma)
    this.tabsEl.innerHTML = BRANCHES.map((b) => {
      const nodes = data.nodes.filter((n) => n.branch === b);
      const open = nodes.filter((n) => n.rank > 0).length;
      const ready = nodes.some((n) => n.state === "available" && data.available >= n.cost);
      return `<button class="tr-tab${b === this.branch ? " active" : ""}${ready ? " ready" : ""}" data-b="${b}" style="--branch-c:${branchColor(b)}">
        <span class="trt-ic">${BRANCH_ICON[b]}</span><span class="trt-n">${i18n.t(`branch.${b}`)}</span><span class="trt-c">${open}/${nodes.length}</span>
      </button>`;
    }).join("");
    this.tabsEl.querySelectorAll<HTMLButtonElement>(".tr-tab").forEach((el) => {
      el.onclick = () => {
        this.branch = el.dataset.b as TreeBranch;
        this.selected = null;
        this.sheet.hidden = true;
        this.render();
      };
    });

    // corpo: uma linha por tier, nós centralizados
    const nodes = data.nodes.filter((n) => n.branch === this.branch);
    const tiers = [...new Set(nodes.map((n) => n.tier))].sort((a, b) => a - b);
    const rows = tiers
      .map((t) => {
        const cells = nodes
          .filter((n) => n.tier === t)
          .map(
            (n) => `<button class="tr-node st-${n.state}${this.selected === n.id ? " sel" : ""}" data-id="${n.id}" style="--branch-c:${branchColor(this.branch)}">
              <span class="trn-ic">${n.state === "locked" ? "🔒" : n.icon}</span>
              <span class="trn-name">${n.name}</span>
              <span class="trn-rank">${n.rank}/${n.maxRank}</span>
            </button>`,
          )
          .join("");
        return `<div class="tr-row" data-tier="${t}">${cells}</div>`;
      })
      .join("");
    // o SVG dos fios vive dentro do container e é medido depois do layout
    this.tree.style.setProperty("--branch-c", branchColor(this.branch));
    this.tree.innerHTML = `<svg class="tr-links" aria-hidden="true"></svg>${rows}`;

    this.tree.querySelectorAll<HTMLButtonElement>(".tr-node").forEach((el) => {
      el.onclick = () => {
        this.selected = el.dataset.id!;
        this.render();
      };
    });

    this.drawLinks(nodes);
    if (this.selected) this.renderSheet(data);
    else this.sheet.hidden = true;
  }

  /** Fios PAI → FILHO medidos no DOM (a árvore fica legível em qualquer largura). */
  private drawLinks(nodes: TreeNodeView[]): void {
    const svg = this.tree.querySelector<SVGSVGElement>(".tr-links");
    if (!svg) return;
    const box = this.tree.getBoundingClientRect();
    const w = this.tree.scrollWidth;
    const hgt = this.tree.scrollHeight;
    svg.setAttribute("viewBox", `0 0 ${w} ${hgt}`);
    svg.setAttribute("width", String(w));
    svg.setAttribute("height", String(hgt));
    const center = (id: string): { x: number; top: number; bottom: number } | null => {
      const el = this.tree.querySelector<HTMLElement>(`.tr-node[data-id="${id}"]`);
      if (!el) return null;
      const r = el.getBoundingClientRect();
      return { x: r.left - box.left + this.tree.scrollLeft + r.width / 2, top: r.top - box.top + this.tree.scrollTop, bottom: r.bottom - box.top + this.tree.scrollTop };
    };
    const byId = new Map(nodes.map((n) => [n.id, n]));
    let paths = "";
    for (const n of nodes) {
      if (!n.parent) continue;
      const p = byId.get(n.parent);
      if (!p) continue;
      const a = center(n.parent);
      const b = center(n.id);
      if (!a || !b) continue;
      const on = p.rank > 0;
      const midY = (a.bottom + b.top) / 2;
      paths += `<path d="M${a.x} ${a.bottom} C ${a.x} ${midY}, ${b.x} ${midY}, ${b.x} ${b.top}" class="trl${on ? " on" : ""}"/>`;
    }
    svg.innerHTML = paths;
  }

  private renderSheet(data: TreeViewData): void {
    const n = data.nodes.find((x) => x.id === this.selected);
    if (!n) {
      this.sheet.hidden = true;
      return;
    }
    const affordable = data.available >= n.cost && n.rank < n.maxRank && n.requires.length === 0;
    const btn =
      n.rank >= n.maxRank
        ? `<button class="btn dim" disabled>${i18n.t("voidTree.maxed")}</button>`
        : n.requires.length > 0
          ? `<button class="btn dim" disabled>${i18n.t("voidTree.requires")}: ${n.requires.join(", ")}</button>`
          : `<button class="btn primary ${affordable ? "" : "dim"}" data-a="unlock">${n.rank > 0 ? i18n.t("voidTree.upgrade") : i18n.t("voidTree.unlock")} · ${n.cost} ✦</button>`;
    // 10.9EG — COMPRA EM LOTE. Um no de rank 50 exigia 50 toques; agora da pra jogar 5 ou tudo
    // o que cabe de uma vez. Os botoes so aparecem quando ha mesmo mais de 1 rank pra comprar.
    const falta = n.maxRank - n.rank;
    const cabe = n.cost > 0 ? Math.floor(data.available / n.cost) : 0;
    const maxLote = Math.max(0, Math.min(falta, cabe));
    const lote = affordable && maxLote > 1
      ? `<div class="trs-bulk">
          ${maxLote >= 5 ? `<button class="btn trs-lote" data-a="buy5">+5 · ${n.cost * 5} ✦</button>` : ""}
          <button class="btn trs-lote" data-a="buymax">${i18n.t("voidTree.max")} +${maxLote} · ${n.cost * maxLote} ✦</button>
        </div>`
      : "";
    this.sheet.innerHTML = `
      <div class="trs-inner void-panel">
        <div class="trs-head"><span class="trs-ic">${n.icon}</span>
          <div><div class="trs-name">${n.name}</div>
          <div class="trs-rank">${i18n.t("voidTree.rank")} ${n.rank}/${n.maxRank}</div></div></div>
        <div class="trs-desc">${n.desc || n.nextDesc}</div>
        ${n.rank < n.maxRank ? `<div class="trs-next"><span>${i18n.t("voidTree.next")}:</span> ${n.nextDesc}</div>` : ""}
        ${n.rank < n.maxRank && n.maxRank > 1 ? `<div class="trs-max"><span>${i18n.t("voidTree.atMax", { n: String(n.maxRank) })}:</span> ${n.maxDesc}</div>` : ""}
        ${lote}
        <div class="trs-actions">${btn}<button class="btn" data-a="close">${i18n.t("menu.back")}</button></div>
      </div>`;
    this.sheet.hidden = false;
    this.sheet.querySelector('[data-a="unlock"]')?.addEventListener("click", () => this.h.onUnlock(n.id, 1));
    this.sheet.querySelector('[data-a="buy5"]')?.addEventListener("click", () => this.h.onUnlock(n.id, 5)); // 10.9EG
    this.sheet.querySelector('[data-a="buymax"]')?.addEventListener("click", () => this.h.onUnlock(n.id, maxLote));
    this.sheet.querySelector('[data-a="close"]')!.addEventListener("click", () => {
      this.selected = null;
      this.sheet.hidden = true;
      this.render();
    });
    this.sheet.addEventListener("click", (e) => {
      if (e.target === this.sheet) {
        this.selected = null;
        this.sheet.hidden = true;
        this.render();
      }
    });
  }

  private showConfirm(): void {
    const cost = this.h.getResetCost?.() ?? 0; // 10.9Y — 1º grátis, depois custa 💎
    this.confirm.querySelector(".trc-text")!.textContent = cost > 0
      ? i18n.t("voidTree.resetConfirmCost", { cost: String(cost) })
      : i18n.t("voidTree.resetConfirmFree");
    this.confirm.querySelector('[data-a="yes"]')!.textContent = cost > 0 ? `💎 ${cost}` : i18n.t("voidTree.reset");
    this.confirm.querySelector('[data-a="no"]')!.textContent = i18n.t("settings.resetNo");
    this.confirm.hidden = false;
  }
}

function branchColor(b: string): string {
  return b === "power" ? "#ff6b9d" : b === "survival" ? "#4cc3ff" : b === "class" ? "#ffd54a" : "#8fef9f";
}
